// Admin Settings page — platform-wide product toggles.
//
// Follows design/UI : UX v1.0/for-engineering-coach-decision-v2/
// (SettingsPage, SettingRow, SettingConfirm, SettingHistory, ExperimentalTag
// per the component inventory). Only one setting ships at launch: the
// sources-visibility toggle in COACH REPLIES. The layout accommodates more
// as they're added.
//
// RBAC: requires settings:read to view, settings:write to flip. The backend
// re-enforces both; this is a convenience render.

import { useEffect, useState } from 'react';
import type { Setting, SettingValuePrimitive } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { listSettingHistory, listSettings, patchSetting } from '../api/endpoints.js';
import { AdminShell, PageHeader } from '../shell/index.js';
import styles from './SettingsPage.module.css';

type SectionKey = Setting['group'];

const SECTION_ORDER: ReadonlyArray<SectionKey> = [
  'coach_replies',
  'content_operations',
  'experimental',
];

export function SettingsPage() {
  const { t } = useI18n();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ok'; settings: Setting[] }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await listSettings();
      if (cancelled) return;
      if (result.ok) {
        setState({ kind: 'ok', settings: [...result.data.settings] });
      } else {
        setState({ kind: 'error', message: t('settings.error.load') });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  function updateSetting(next: Setting) {
    setState((prev) =>
      prev.kind === 'ok'
        ? { kind: 'ok', settings: prev.settings.map((s) => (s.key === next.key ? next : s)) }
        : prev,
    );
  }

  return (
    <AdminShell activeKey="settings" breadcrumb={['Admin', t('settings.title')]}>
      <PageHeader title={t('settings.title')} sub={t('settings.lead')} />
      {state.kind === 'loading' && (
        <div className={styles.loading}>
          <div className={styles.sk} style={{ width: '30%' }} />
          <div className={styles.sk} style={{ width: '90%', marginTop: 18 }} />
          <div className={styles.sk} style={{ width: '70%' }} />
        </div>
      )}
      {state.kind === 'error' && (
        <div className={styles.err}>{state.message}</div>
      )}
      {state.kind === 'ok' && (
        <SettingsBody settings={state.settings} onUpdate={updateSetting} />
      )}
    </AdminShell>
  );
}

function SettingsBody({
  settings,
  onUpdate,
}: {
  settings: readonly Setting[];
  onUpdate: (s: Setting) => void;
}) {
  const { t } = useI18n();
  const bySection: Record<SectionKey, Setting[]> = {
    coach_replies: [],
    content_operations: [],
    experimental: [],
  };
  for (const s of settings) bySection[s.group].push(s);

  return (
    <div className={styles.wrap}>
      {SECTION_ORDER.filter((key) => bySection[key].length > 0).map((key) => (
        <section key={key} className={styles.group}>
          <h2 className={styles.groupHeader}>{t(`settings.group.${sectionI18nKey(key)}`)}</h2>
          {bySection[key].map((s) => (
            <SettingRow key={s.key} setting={s} onUpdate={onUpdate} />
          ))}
        </section>
      ))}
    </div>
  );
}

function sectionI18nKey(key: SectionKey): string {
  switch (key) {
    case 'coach_replies':
      return 'coach';
    case 'content_operations':
      return 'content';
    case 'experimental':
      return 'experimental';
  }
}

// ─── SettingRow + confirm panel + history ──────────────────────────────────

function SettingRow({
  setting,
  onUpdate,
}: {
  setting: Setting;
  onUpdate: (s: Setting) => void;
}) {
  const { t, tt } = useI18n();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isBool = setting.type === 'boolean';
  const value = setting.value;
  const isOn = isBool && value === true;
  const nextValueOnFlip: SettingValuePrimitive = isBool ? !isOn : value;

  async function commit() {
    setSaving(true);
    setSaveError(null);
    const result = await patchSetting(setting.key, { value: nextValueOnFlip });
    setSaving(false);
    if (result.ok) {
      onUpdate(result.data.setting);
      setConfirmOpen(false);
    } else {
      setSaveError(t('settings.error.save'));
    }
  }

  const auditLine = setting.lastChangedByName && setting.lastChangedAt
    ? tt('settings.audit', {
        name: setting.lastChangedByName,
        when: formatRelative(setting.lastChangedAt),
      })
    : t('settings.neverChanged');

  const confirmTitleKey = isOn ? setting.confirmOffTitleKey : setting.confirmOnTitleKey;
  const confirmBodyKey = isOn ? setting.confirmOffBodyKey : setting.confirmOnBodyKey;
  const confirmActionLabel = isOn
    ? t('settings.sources.hide')
    : t('settings.sources.show');

  return (
    <div className={styles.row}>
      <div className={styles.rowBody}>
        <div className={styles.rowLabel}>
          <span>{t(setting.labelKey)}</span>
          {setting.experimental && (
            <span className={styles.experimentalTag}>{t('settings.tag.experimental')}</span>
          )}
        </div>
        <div className={styles.rowDesc}>{t(setting.descKey)}</div>
        {setting.learningKey && (
          <div className={styles.rowLearning}>{t(setting.learningKey)}</div>
        )}
        <div className={styles.rowEffect}>{t(setting.effectKey)}</div>
        <div className={styles.rowAudit}>
          {auditLine}
          {' · '}
          <button
            type="button"
            className={styles.inlineLink}
            onClick={() => setHistoryOpen((h) => !h)}
          >
            {historyOpen ? t('settings.hideHistory') : t('settings.history')}
          </button>
        </div>
        {historyOpen && <SettingHistoryPanel settingKey={setting.key} />}
        {confirmOpen && confirmTitleKey && confirmBodyKey && (
          <div className={styles.confirmPanel}>
            <div className={styles.confirmTitle}>{t(confirmTitleKey)}</div>
            <div className={styles.confirmBody}>{t(confirmBodyKey)}</div>
            {saveError && <div className={styles.confirmError}>{saveError}</div>}
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnBrand}`}
                onClick={() => void commit()}
                disabled={saving}
              >
                {confirmActionLabel}
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={() => {
                  setConfirmOpen(false);
                  setSaveError(null);
                }}
                disabled={saving}
              >
                {t('settings.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
      {isBool && (
        <div className={styles.rowControl}>
          <button
            type="button"
            role="switch"
            aria-checked={isOn}
            aria-label={t(setting.labelKey)}
            className={`${styles.switch} ${isOn ? styles.switchOn : ''}`}
            onClick={() => setConfirmOpen((o) => !o)}
          />
          <div className={styles.switchLabel}>
            {isOn ? t('settings.state.on') : t('settings.state.off')}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingHistoryPanel({ settingKey }: { settingKey: string }) {
  const { t, tt } = useI18n();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ok'; entries: Array<{ when: string; name: string; stateLabel: string }> }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await listSettingHistory(settingKey);
      if (cancelled) return;
      if (result.ok) {
        const entries = result.data.history.map((e) => ({
          when: formatAbsolute(e.changedAt),
          name: e.changedByName,
          stateLabel:
            typeof e.newValue === 'boolean'
              ? e.newValue
                ? t('settings.state.on').toLowerCase()
                : t('settings.state.off').toLowerCase()
              : String(e.newValue),
        }));
        setState({ kind: 'ok', entries });
      } else {
        setState({ kind: 'error', message: t('settings.error.load') });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settingKey, t]);

  if (state.kind === 'loading') {
    return <div className={styles.historyLoading}>{t('settings.historyLoading')}</div>;
  }
  if (state.kind === 'error') {
    return <div className={styles.historyError}>{state.message}</div>;
  }
  if (state.entries.length === 0) {
    return <div className={styles.historyEmpty}>{t('settings.historyEmpty')}</div>;
  }
  return (
    <div className={styles.historyPanel}>
      {state.entries.map((e, i) => (
        <div key={i} className={styles.historyEntry}>
          <span className={styles.historyWhen}>{e.when}</span>
          <span>{tt('settings.historyEntry', { name: e.name, state: e.stateLabel })}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Date formatters ───────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  const diffMs = Date.now() - Date.parse(iso);
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

